import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings as SettingsIcon, Activity, Clock, AlertTriangle, Save, TestTube, Trash2, Database, Heart, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Settings() {
  const [apifyToken, setApifyToken] = useState('');
  
  // Unified Tracking Config
  const [autoTrackingEnabled, setAutoTrackingEnabled] = useState(true);
  const [masterInterval, setMasterInterval] = useState(10);
  const [activeFreq, setActiveFreq] = useState(10);
  const [decliningFreq, setDecliningFreq] = useState(15);
  const [deadFreq, setDeadFreq] = useState(60);
  const [decliningThreshold, setDecliningThreshold] = useState(50);
  const [deadThreshold, setDeadThreshold] = useState(10);
  
  const [enableTracking, setEnableTracking] = useState(true);
  const [emailAlerts, setEmailAlerts] = useState('');
  const [slackWebhook, setSlackWebhook] = useState('');
  const [testing, setTesting] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  
  // Tracking Cutoff State
  const [cutoffDate, setCutoffDate] = useState('');
  const [cutoffEnabled, setCutoffEnabled] = useState(false);
  const [cutoffStats, setCutoffStats] = useState(null);
  const [applyingCutoff, setApplyingCutoff] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);
  const [testingConnections, setTestingConnections] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Quality Score Configuration
  const [engagementWeight, setEngagementWeight] = useState(30);
  const [viewsToVisitsWeight, setViewsToVisitsWeight] = useState(30);
  const [visitsToSignupsWeight, setVisitsToSignupsWeight] = useState(40);
  
  // Engagement Rate Configuration
  const [likesWeight, setLikesWeight] = useState(50);
  const [commentsWeight, setCommentsWeight] = useState(30);
  const [sharesWeight, setSharesWeight] = useState(20);

  useEffect(() => {
    fetchAllConfigs();
    fetchDataSummary();
    fetchSchedulerStatus();
    fetchTrackingCutoff();
  }, []);

  const fetchAllConfigs = async () => {
    try {
      // Fetch unified tracking config
      const trackingResponse = await axios.get(`${API}/admin/tracking-config`);
      const trackingConfig = trackingResponse.data;
      
      setAutoTrackingEnabled(trackingConfig.auto_tracking_enabled);
      setMasterInterval(trackingConfig.master_interval_minutes);
      setActiveFreq(trackingConfig.active_frequency_minutes);
      setDecliningFreq(trackingConfig.declining_frequency_minutes);
      setDeadFreq(trackingConfig.dead_frequency_minutes);
      setDecliningThreshold(trackingConfig.declining_threshold_percent);
      setDeadThreshold(trackingConfig.dead_threshold_percent);
      
      // Fetch other configs
      const response = await axios.get(`${API}/admin/config`);
      const config = response.data;
      
      // Quality Score
      setEngagementWeight(config.quality_score.engagement_weight * 100);
      setViewsToVisitsWeight(config.quality_score.views_to_visits_weight * 100);
      setVisitsToSignupsWeight(config.quality_score.visits_to_signups_weight * 100);
      
      // Engagement Rate
      setLikesWeight(config.engagement_rate.likes_weight * 100);
      setCommentsWeight(config.engagement_rate.comments_weight * 100);
      setSharesWeight(config.engagement_rate.shares_weight * 100);
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Failed to load configurations');
    }
  };

  const fetchDataSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/data-summary`);
      setDataSummary(response.data);
    } catch (error) {
      console.error('Error fetching data summary:', error);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      setLoadingScheduler(true);
      const response = await axios.get(`${API}/admin/scheduler-status`);
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
      toast.error('Failed to load scheduler status');
    } finally {
      setLoadingScheduler(false);
    }
  };

  const handlePauseScheduler = async () => {
    try {
      const response = await axios.post(`${API}/admin/scheduler-pause`);
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchSchedulerStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error pausing scheduler:', error);
      toast.error('Failed to pause scheduler');
    }
  };

  const handleResumeScheduler = async () => {
    try {
      const response = await axios.post(`${API}/admin/scheduler-resume`);
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchSchedulerStatus();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error resuming scheduler:', error);
      toast.error('Failed to resume scheduler');
    }
  };


  // handleTestConnection replaced with handleTestApifyConnection

  const handleSaveQualityScore = async () => {
    try {
      const total = engagementWeight + viewsToVisitsWeight + visitsToSignupsWeight;
      if (Math.abs(total - 100) > 1) {
        toast.error(`Quality score weights must sum to 100% (currently ${total}%)`);
        return;
      }
      
      await axios.post(`${API}/admin/config/quality-score`, {
        engagement_weight: engagementWeight / 100,
        views_to_visits_weight: viewsToVisitsWeight / 100,
        visits_to_signups_weight: visitsToSignupsWeight / 100
      });
      
      toast.success('Quality score configuration saved!');
    } catch (error) {
      toast.error('Failed to save quality score config');
      console.error('Error saving quality score config:', error);
    }
  };

  const handleSaveEngagementRate = async () => {
    try {
      const total = likesWeight + commentsWeight + sharesWeight;
      if (Math.abs(total - 100) > 1) {
        toast.error(`Engagement rate weights must sum to 100% (currently ${total}%)`);
        return;
      }
      
      await axios.post(`${API}/admin/config/engagement-rate`, {
        likes_weight: likesWeight / 100,
        comments_weight: commentsWeight / 100,
        shares_weight: sharesWeight / 100
      });
      
      toast.success('Engagement rate configuration saved!');
    } catch (error) {
      toast.error('Failed to save engagement rate config');
      console.error('Error saving engagement rate config:', error);
    }
  };

  const handleSaveThresholds = async () => {
    // This is now handled by handleSaveTrackingConfig
    await handleSaveTrackingConfig();
  };

  const handleSaveTrackingConfig = async () => {
    try {
      // Validations
      if (activeFreq <= 0 || decliningFreq <= 0 || deadFreq <= 0 || masterInterval <= 0) {
        toast.error('All frequency values must be positive');
        return;
      }
      
      if (!(activeFreq <= decliningFreq && decliningFreq <= deadFreq)) {
        toast.error('Frequencies must follow: Active ≤ Declining ≤ Dead');
        return;
      }
      
      const minFreq = Math.min(activeFreq, decliningFreq, deadFreq);
      if (masterInterval > minFreq) {
        toast.error(`Master interval (${masterInterval} min) must be ≤ minimum frequency (${minFreq} min)`);
        return;
      }
      
      if (decliningThreshold <= deadThreshold) {
        toast.error('Declining threshold must be greater than dead threshold');
        return;
      }
      
      const response = await axios.post(`${API}/admin/tracking-config`, {
        auto_tracking_enabled: autoTrackingEnabled,
        master_interval_minutes: masterInterval,
        active_frequency_minutes: activeFreq,
        declining_frequency_minutes: decliningFreq,
        dead_frequency_minutes: deadFreq,
        declining_threshold_percent: decliningThreshold,
        dead_threshold_percent: deadThreshold
      });
      
      toast.success('Tracking configuration saved! Changes will apply on next scheduled run.');
      
      // Refresh scheduler status
      await fetchSchedulerStatus();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save tracking configuration';
      toast.error(errorMsg);
      console.error('Error saving tracking config:', error);
    }
  };

  const handleSaveCronFrequencies = async () => {
    // This is now handled by handleSaveTrackingConfig
    await handleSaveTrackingConfig();
  };


  // Tracking Cutoff Functions
  const fetchTrackingCutoff = async () => {
    try {
      const response = await axios.get(`${API}/admin/tracking-cutoff`);
      setCutoffDate(response.data.cutoff_date ? response.data.cutoff_date.split('T')[0] : '');
      setCutoffEnabled(response.data.enabled);
      setCutoffStats(response.data);
    } catch (error) {
      console.error('Error fetching tracking cutoff:', error);
    }
  };

  const handleApplyCutoff = async () => {
    if (!cutoffDate) {
      toast.error('Please select a cutoff date');
      return;
    }

    setApplyingCutoff(true);
    try {
      const response = await axios.post(`${API}/admin/tracking-cutoff`, {
        cutoff_date: new Date(cutoffDate + 'T00:00:00Z').toISOString()
      });

      setCutoffStats(response.data);
      
      // Show appropriate message based on what happened
      const { videos_archived, videos_reactivated } = response.data;
      let message = '';

      if (videos_archived > 0 && videos_reactivated > 0) {
        message = `✅ Archived ${videos_archived} videos, ▶️ Reactivated ${videos_reactivated} videos`;
      } else if (videos_archived > 0) {
        message = `✅ Archived ${videos_archived} videos older than ${new Date(cutoffDate).toLocaleDateString()}`;
      } else if (videos_reactivated > 0) {
        message = `▶️ Reactivated ${videos_reactivated} videos newer than ${new Date(cutoffDate).toLocaleDateString()}`;
      } else {
        message = `✓ Cutoff date updated. No videos affected.`;
      }

      toast.success(message);
      
      // Refresh data summary
      await fetchDataSummary();
    } catch (error) {
      toast.error('Failed to apply cutoff date');
      console.error('Error applying cutoff:', error);
    } finally {
      setApplyingCutoff(false);
    }
  };


  const handleTestApifyConnection = async () => {
    try {
      setTesting(true);
      const response = await axios.get(`${API}/api-health`);
      const actors = response.data;
      
      const working = actors.filter(a => a.success_rate > 0);
      const failed = actors.filter(a => a.success_rate === 0);
      
      if (failed.length === 0) {
        toast.success(`✅ All Apify actors working! (${working.length}/${actors.length})`);
      } else {
        toast.warning(`⚠️ ${working.length}/${actors.length} actors working. ${failed.length} failing.`);
      }
    } catch (error) {
      toast.error('Failed to test Apify connection');
      console.error('Test error:', error);
    } finally {
      setTesting(false);
    }
  };

  const handleTestAllConnections = async () => {
    try {
      setTestingConnections(true);
      const response = await axios.get(`${API}/admin/test-connections`);
      setConnectionStatus(response.data.services);
      
      // Show summary toast
      const services = response.data.services;
      const connected = Object.values(services).filter(s => s.status === 'connected').length;
      const total = Object.keys(services).length;
      
      if (connected === total) {
        toast.success(`✅ All services connected (${connected}/${total})`);
      } else {
        toast.warning(`⚠️ ${connected}/${total} services connected. Check details below.`);
      }
      
      // Store full response with timestamp
      setConnectionStatus({ ...response.data.services, timestamp: response.data.timestamp });
    } catch (error) {
      toast.error('Failed to test connections');
      console.error('Test error:', error);
    } finally {
      setTestingConnections(false);
    }
  };

  const handleClearTestData = async () => {
    if (!window.confirm('⚠️ This will DELETE ALL videos, metrics, and events from the database. Are you sure?')) {
      return;
    }
    
    try {
      setClearing(true);
      const response = await axios.delete(`${API}/admin/clear-test-data`);
      toast.success(`Cleared ${response.data.deleted.videos} videos and all related data`);
      await fetchDataSummary();
    } catch (error) {
      toast.error('Failed to clear test data');
    } finally {
      setClearing(false);
    }
  };

  const handleResetTracking = async () => {
    if (!window.confirm('⚠️ This will RESET all tracking data (metrics history) but keep your videos. Start fresh with 15-minute intervals. Continue?')) {
      return;
    }
    
    try {
      setClearing(true);
      const response = await axios.post(`${API}/admin/reset-tracking`);
      toast.success(response.data.message);
      await fetchDataSummary();
    } catch (error) {
      toast.error('Failed to reset tracking');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-8 py-6" data-testid="settings-container">
      {/* Header */}
      <div className="max-w-[1600px] mx-auto">
        <h1 className="text-3xl font-bold text-gray-900" data-testid="settings-title">Settings</h1>
        <p className="text-gray-600 mt-1">Configure tracking and alerts</p>
      </div>

      <div className="max-w-[1600px] mx-auto mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Management */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="data-management-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Database className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Data Management</h2>
              <p className="text-sm text-gray-500">Test vs Real data</p>
            </div>
          </div>

          {dataSummary && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500 text-sm mb-1">Total Videos</p>
                  <p className="text-2xl font-bold text-gray-900">{dataSummary.total_videos}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500 text-sm mb-1">Tracked Videos</p>
                  <p className="text-2xl font-bold text-green-600">{dataSummary.tracked_videos}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500 text-sm mb-1">Real API Metrics</p>
                  <p className="text-2xl font-bold text-blue-600">{dataSummary.real_api_metrics}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500 text-sm mb-1">Test Metrics</p>
                  <p className="text-2xl font-bold text-yellow-600">{dataSummary.test_metrics}</p>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 mb-2">
                  <strong>📊 Data Status:</strong>
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>Tracked videos:</strong> Have real Apify data</li>
                  <li>• <strong>Untracked:</strong> Never fetched via Apify (test data)</li>
                  <li>• <strong>Real API metrics:</strong> From Apify scrapers</li>
                  <li>• <strong>Test metrics:</strong> From seed script (fake data)</li>
                </ul>
              </div>

              <Button
                onClick={handleClearTestData}
                disabled={clearing}
                data-testid="clear-test-data-btn"
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700 text-white mb-3"
              >
                {clearing ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Clearing...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <Trash2 className="w-4 h-4" />
                    <span>Clear All Test Data</span>
                  </span>
                )}
              </Button>
              
              <Button
                onClick={handleResetTracking}
                disabled={clearing}
                variant="outline"
                className="w-full border-yellow-500 text-yellow-700 hover:bg-yellow-50"
              >
                {clearing ? (
                  <span className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                    <span>Resetting...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <Activity className="w-4 h-4" />
                    <span>Reset Tracking (Keep Videos)</span>
                  </span>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Apify Configuration */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="apify-config-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Apify Configuration</h2>
              <p className="text-sm text-gray-500">API credentials and actors</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="apify-token" className="text-gray-700">API Token</Label>
              <Input
                id="apify-token"
                data-testid="apify-token-input"
                type="password"
                placeholder="apify_api_xxxxxxxxxxxxx"
                value={apifyToken}
                onChange={(e) => setApifyToken(e.target.value)}
                className="mt-2 bg-white border-gray-300 text-gray-900"
              />
            </div>

            <div>
              <Label className="text-gray-700 mb-2 block">Active Actors</Label>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-gray-700">Instagram</span>
                  <code className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">apify/instagram-reel-scraper</code>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-gray-700">TikTok</span>
                  <code className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">clockworks/tiktok-video-scraper</code>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-gray-700">YouTube</span>
                  <code className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">streamers/youtube-scraper</code>
                </div>
              </div>
            </div>

            <Button
              onClick={handleTestApifyConnection}
              disabled={testing}
              data-testid="test-connection-button"
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {testing ? (
                <span className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span>Testing...</span>
                </span>
              ) : (
                <span className="flex items-center space-x-2">
                  <TestTube className="w-4 h-4" />
                  <span>Test Connection</span>
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Tracking Cutoff (Archive Old Videos) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="tracking-cutoff-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <Database className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Video Tracking Cutoff</h2>
              <p className="text-sm text-gray-500">Stop tracking videos older than a specific date to save credits</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                💡 Videos with <code className="bg-blue-100 px-1 py-0.5 rounded text-blue-700">go_live_timestamp</code> before the selected date will be marked as <strong>ARCHIVED</strong> and excluded from cron tracking.
              </p>
            </div>

            <div>
              <Label className="text-gray-700 mb-2">
                Stop tracking videos that went live before:
              </Label>
              <div className="flex items-center space-x-4 mt-2">
                <input
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                />
                <Button
                  onClick={handleApplyCutoff}
                  disabled={applyingCutoff || !cutoffDate}
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white"
                >
                  {applyingCutoff ? 'Applying...' : 'Apply Cutoff Date'}
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Videos before this date will be marked as ARCHIVED and won't be scraped by Apify
              </p>
            </div>

            {cutoffStats && cutoffStats.cutoff_date && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">
                  ✅ Currently archiving videos before: <strong>{new Date(cutoffStats.cutoff_date).toLocaleDateString()}</strong>
                  <br />
                  📦 Total archived: <strong>{cutoffStats.total_archived_now || cutoffStats.videos_archived || 0}</strong> videos
                </p>
                {cutoffStats.videos_reactivated > 0 && (
                  <p className="text-blue-700 text-sm mt-2">
                    ▶️ Last update reactivated: <strong>{cutoffStats.videos_reactivated}</strong> videos
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tracking Frequency */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="tracking-frequency-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Tracking Frequency</h2>
              <p className="text-sm text-gray-500">Configure cron intervals and auto-tracking</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Auto-tracking Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <Label className="text-gray-700 font-semibold">Enable Automatic Tracking</Label>
                <p className="text-xs text-gray-500 mt-1">Turn on/off scheduled video tracking</p>
              </div>
              <Switch
                checked={autoTrackingEnabled}
                onCheckedChange={setAutoTrackingEnabled}
                data-testid="enable-tracking-switch"
              />
            </div>

            {/* Master Interval */}
            <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-cyan-800 font-semibold">Master Interval (Cron Frequency)</Label>
                  <p className="text-xs text-gray-600 mt-1">How often the scheduler checks all videos</p>
                </div>
                <span className="text-cyan-700 font-bold text-lg">{masterInterval} mins</span>
              </div>
              <Slider
                value={[masterInterval]}
                onValueChange={([value]) => setMasterInterval(value)}
                min={5}
                max={30}
                step={5}
                data-testid="master-interval-slider"
                className="w-full mt-2"
              />
              <p className="text-xs text-cyan-700 mt-2">
                ⚠️ Must be ≤ minimum of all video frequencies. Runs at fixed times (e.g., every 10 min: 6:00, 6:10, 6:20...)
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Active Videos</Label>
                <span className="text-gray-900 font-semibold">{activeFreq} mins</span>
              </div>
              <Slider
                value={[activeFreq]}
                onValueChange={([value]) => setActiveFreq(value)}
                min={5}
                max={60}
                step={5}
                data-testid="active-freq-slider"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Track active videos every {activeFreq} minutes</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Declining Videos</Label>
                <span className="text-gray-900 font-semibold">{decliningFreq} mins</span>
              </div>
              <Slider
                value={[decliningFreq]}
                onValueChange={([value]) => setDecliningFreq(value)}
                min={10}
                max={120}
                step={5}
                data-testid="declining-freq-slider"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Track declining videos every {decliningFreq} minutes</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Dead Videos</Label>
                <span className="text-gray-900 font-semibold">{deadFreq} mins</span>
              </div>
              <Slider
                value={[deadFreq]}
                onValueChange={([value]) => setDeadFreq(value)}
                min={30}
                max={480}
                step={30}
                data-testid="dead-freq-slider"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Track dead videos every {deadFreq} minutes</p>
            </div>

            <Button
              onClick={handleSaveTrackingConfig}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-frequencies-button"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Tracking Configuration
            </Button>
          </div>
        </div>

        {/* Death Detection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="death-detection-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Death Detection</h2>
              <p className="text-sm text-gray-500">Status thresholds</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Declining Threshold</Label>
                <span className="text-yellow-600 font-semibold">{decliningThreshold}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Mark as DECLINING when growth rate drops below this % of peak growth rate</p>
              <Slider
                value={[decliningThreshold]}
                onValueChange={([value]) => setDecliningThreshold(value)}
                min={10}
                max={90}
                step={5}
                data-testid="declining-threshold-slider"
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Dead Threshold</Label>
                <span className="text-red-600 font-semibold">{deadThreshold}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Mark as DEAD when growth rate drops below this % of peak growth rate</p>
              <Slider
                value={[deadThreshold]}
                onValueChange={([value]) => setDeadThreshold(value)}
                min={1}
                max={50}
                step={1}
                data-testid="dead-threshold-slider"
                className="w-full"
              />
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                <strong>💡 How Death Detection Works:</strong>
              </p>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>• Videos are marked based on <strong>growth momentum</strong>, not total views</li>
                <li>• Growth rate = new views gained per 15-minute interval</li>
                <li>• Peak growth rate = highest growth ever achieved by the video</li>
                <li>• Example: 1M views with 0 new views = DEAD | 1K views with 100 new views = ACTIVE</li>
              </ul>
            </div>

            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>Current Settings:</strong><br />
                Growth rate &lt; {decliningThreshold}% of peak growth → 🟡 DECLINING<br />
                Growth rate &lt; {deadThreshold}% of peak growth → 🔴 DEAD
              </p>
            </div>

            <Button
              onClick={handleSaveThresholds}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="save-thresholds-button"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Death Detection Thresholds
            </Button>
          </div>
        </div>

        {/* Scheduler Monitoring */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="scheduler-monitoring-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Scheduler Status</h2>
                <p className="text-sm text-gray-500">Monitor cron job execution</p>
              </div>
            </div>
            <Button
              onClick={fetchSchedulerStatus}
              disabled={loadingScheduler}
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingScheduler ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loadingScheduler ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-2"></div>
              <p>Loading scheduler status...</p>
            </div>
          ) : schedulerStatus ? (
            <div className="space-y-4">
              {/* Scheduler Running Status */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Scheduler Engine</div>
                    <div className="flex items-center space-x-2">
                      {schedulerStatus.scheduler_running ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-green-600 font-semibold">Running</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="text-red-600 font-semibold">Stopped</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Tracking Job</div>
                    <div className="flex items-center space-x-2">
                      {schedulerStatus.tracking_job_active ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-green-600 font-semibold">Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-orange-600" />
                          <span className="text-orange-600 font-semibold">Paused</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Pause/Resume Controls */}
                <div className="flex gap-2 mt-3">
                  {schedulerStatus.tracking_job_active ? (
                    <Button
                      onClick={handlePauseScheduler}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50"
                    >
                      ⏸️ Pause Tracking
                    </Button>
                  ) : (
                    <Button
                      onClick={handleResumeScheduler}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                    >
                      ▶️ Resume Tracking
                    </Button>
                  )}
                  <Button
                    onClick={fetchSchedulerStatus}
                    variant="outline"
                    size="sm"
                    className="border-cyan-300 text-cyan-600 hover:bg-cyan-50"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Master Interval */}
              <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <span className="text-gray-700">Master Tracking Interval</span>
                <span className="text-cyan-700 font-semibold text-lg">{schedulerStatus.master_interval_minutes} min</span>
              </div>

              {/* Video Status Counts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                  <div className="text-2xl font-bold text-green-600">{schedulerStatus.video_status_counts.active}</div>
                  <div className="text-xs text-gray-600 mt-1">Active Videos</div>
                  <div className="text-xs text-green-600 mt-1">{schedulerStatus.configured_frequencies.active_frequency}min</div>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{schedulerStatus.video_status_counts.declining}</div>
                  <div className="text-xs text-gray-600 mt-1">Declining</div>
                  <div className="text-xs text-yellow-600 mt-1">{schedulerStatus.configured_frequencies.declining_frequency}min</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <div className="text-2xl font-bold text-red-600">{schedulerStatus.video_status_counts.dead}</div>
                  <div className="text-xs text-gray-600 mt-1">Dead Videos</div>
                  <div className="text-xs text-red-600 mt-1">{schedulerStatus.configured_frequencies.dead_frequency}min</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                  <div className="text-2xl font-bold text-blue-600">{schedulerStatus.video_status_counts.total}</div>
                  <div className="text-xs text-gray-600 mt-1">Total Videos</div>
                </div>
              </div>

              {/* Death Detection Thresholds */}
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h3 className="text-sm font-semibold text-orange-700 mb-2">Death Detection Thresholds (Active)</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Declining:</span>
                    <span className="text-yellow-600 font-semibold">{(schedulerStatus.death_detection_thresholds.declining_threshold * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dead:</span>
                    <span className="text-red-600 font-semibold">{(schedulerStatus.death_detection_thresholds.dead_threshold * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>

              {/* Scheduled Jobs */}
              {schedulerStatus.scheduled_jobs && schedulerStatus.scheduled_jobs.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Scheduled Jobs</h3>
                  <div className="space-y-2">
                    {schedulerStatus.scheduled_jobs.map((job, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white rounded border border-gray-100">
                        <span className="text-gray-700">{job.name}</span>
                        <span className="text-cyan-600 text-xs">
                          {job.next_run_time ? new Date(job.next_run_time).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Last Updated */}
              <div className="text-center text-xs text-gray-500 mt-4">
                Last updated: {new Date(schedulerStatus.last_updated).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Unable to load scheduler status</p>
            </div>
          )}
        </div>

        {/* Service Connections */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="service-connections-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Service Connections</h2>
                <p className="text-sm text-gray-500">Test external service integrations</p>
              </div>
            </div>
            <Button
              onClick={handleTestAllConnections}
              disabled={testingConnections}
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <TestTube className={`w-4 h-4 mr-2 ${testingConnections ? 'animate-spin' : ''}`} />
              Test All
            </Button>
          </div>

          {testingConnections ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
              <p>Testing connections...</p>
            </div>
          ) : connectionStatus ? (
            <div className="space-y-3">
              {/* MongoDB */}
              <div className={`p-4 rounded-lg border ${
                connectionStatus.mongodb.status === 'connected' 
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Database className={`w-5 h-5 ${
                      connectionStatus.mongodb.status === 'connected' ? 'text-green-600' : 'text-red-600'
                    }`} />
                    <div>
                      <div className="text-gray-900 font-semibold">MongoDB</div>
                      <div className="text-xs text-gray-600 mt-1">{connectionStatus.mongodb.message}</div>
                    </div>
                  </div>
                  {connectionStatus.mongodb.status === 'connected' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </div>

              {/* Apify */}
              <div className={`p-4 rounded-lg border ${
                connectionStatus.apify.status === 'connected' 
                  ? 'bg-green-50 border-green-200'
                  : connectionStatus.apify.status === 'warning'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Activity className={`w-5 h-5 ${
                      connectionStatus.apify.status === 'connected' 
                        ? 'text-green-600' 
                        : connectionStatus.apify.status === 'warning'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`} />
                    <div>
                      <div className="text-gray-900 font-semibold">Apify API</div>
                      <div className="text-xs text-gray-600 mt-1">{connectionStatus.apify.message}</div>
                    </div>
                  </div>
                  {connectionStatus.apify.status === 'connected' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : connectionStatus.apify.status === 'warning' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </div>

              {/* ipapi.co */}
              <div className={`p-4 rounded-lg border ${
                connectionStatus.ipapi.status === 'connected' 
                  ? 'bg-green-50 border-green-200'
                  : connectionStatus.ipapi.status === 'warning'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Activity className={`w-5 h-5 ${
                      connectionStatus.ipapi.status === 'connected' 
                        ? 'text-green-600'
                        : connectionStatus.ipapi.status === 'warning'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`} />
                    <div>
                      <div className="text-gray-900 font-semibold">ipapi.co (Geolocation)</div>
                      <div className="text-xs text-gray-600 mt-1">{connectionStatus.ipapi.message}</div>
                    </div>
                  </div>
                  {connectionStatus.ipapi.status === 'connected' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : connectionStatus.ipapi.status === 'warning' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </div>

              {/* Last Updated */}
              <div className="text-center text-xs text-gray-500 mt-4">
                Last tested: {new Date(connectionStatus.timestamp).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <TestTube className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Click &quot;Test All&quot; to check service connections</p>
            </div>
          )}
        </div>

        {/* Quality Score Configuration */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="quality-score-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Quality Score Configuration</h2>
              <p className="text-sm text-gray-500">Adjust scoring weights for influencer ranking</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Engagement Weight</Label>
                <span className="text-indigo-600 font-semibold">{engagementWeight}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Weight for likes, comments, shares in quality score</p>
              <Slider
                value={[engagementWeight]}
                onValueChange={([value]) => setEngagementWeight(value)}
                min={0}
                max={100}
                step={5}
                data-testid="engagement-weight-slider"
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Views to Visits Weight</Label>
                <span className="text-indigo-600 font-semibold">{viewsToVisitsWeight}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Weight for conversion from views to website visits</p>
              <Slider
                value={[viewsToVisitsWeight]}
                onValueChange={([value]) => setViewsToVisitsWeight(value)}
                min={0}
                max={100}
                step={5}
                data-testid="views-to-visits-weight-slider"
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Visits to Signups Weight</Label>
                <span className="text-indigo-600 font-semibold">{visitsToSignupsWeight}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Weight for conversion from visits to signups</p>
              <Slider
                value={[visitsToSignupsWeight]}
                onValueChange={([value]) => setVisitsToSignupsWeight(value)}
                min={0}
                max={100}
                step={5}
                data-testid="visits-to-signups-weight-slider"
                className="w-full"
              />
            </div>

            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-800 mb-2">
                <strong>Current Weights:</strong>
              </p>
              <div className="text-sm text-gray-700 space-y-1">
                <div className="flex justify-between">
                  <span>Engagement:</span>
                  <span className="text-indigo-600">{engagementWeight}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Views → Visits:</span>
                  <span className="text-indigo-600">{viewsToVisitsWeight}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Visits → Signups:</span>
                  <span className="text-indigo-600">{visitsToSignupsWeight}%</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-indigo-200 pt-1 mt-2">
                  <span>Total:</span>
                  <span className="text-indigo-600">{engagementWeight + viewsToVisitsWeight + visitsToSignupsWeight}%</span>
                </div>
              </div>
              {engagementWeight + viewsToVisitsWeight + visitsToSignupsWeight !== 100 && (
                <p className="text-xs text-yellow-700 mt-2">
                  ⚠️ Weights should total 100% for optimal scoring
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Engagement Rate Configuration */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="engagement-rate-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Engagement Rate Configuration</h2>
              <p className="text-sm text-gray-500">Adjust weights for engagement components</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Likes Weight</Label>
                <span className="text-pink-600 font-semibold">{likesWeight}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Weight for likes in engagement calculation</p>
              <Slider
                value={[likesWeight]}
                onValueChange={([value]) => setLikesWeight(value)}
                min={0}
                max={100}
                step={5}
                data-testid="likes-weight-slider"
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Comments Weight</Label>
                <span className="text-pink-600 font-semibold">{commentsWeight}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Weight for comments in engagement calculation</p>
              <Slider
                value={[commentsWeight]}
                onValueChange={([value]) => setCommentsWeight(value)}
                min={0}
                max={100}
                step={5}
                data-testid="comments-weight-slider"
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Shares Weight</Label>
                <span className="text-pink-600 font-semibold">{sharesWeight}%</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">Weight for shares in engagement calculation</p>
              <Slider
                value={[sharesWeight]}
                onValueChange={([value]) => setSharesWeight(value)}
                min={0}
                max={100}
                step={5}
                data-testid="shares-weight-slider"
                className="w-full"
              />
            </div>

            <div className="p-4 bg-pink-50 border border-pink-200 rounded-lg">
              <p className="text-sm text-pink-800 mb-2">
                <strong>Engagement Rate Formula:</strong>
              </p>
              <p className="text-xs text-gray-700 mb-3">
                (likes × {likesWeight}% + comments × {commentsWeight}% + shares × {sharesWeight}%) / views
              </p>
              <div className="text-sm text-gray-700 space-y-1">
                <div className="flex justify-between">
                  <span>Likes:</span>
                  <span className="text-pink-600">{likesWeight}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Comments:</span>
                  <span className="text-pink-600">{commentsWeight}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Shares:</span>
                  <span className="text-pink-600">{sharesWeight}%</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-pink-200 pt-1 mt-2">
                  <span>Total:</span>
                  <span className="text-pink-600">{likesWeight + commentsWeight + sharesWeight}%</span>
                </div>
              </div>
              {likesWeight + commentsWeight + sharesWeight !== 100 && (
                <p className="text-xs text-yellow-700 mt-2">
                  ⚠️ Weights should total 100%
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Geographic Heatmap Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="geographic-settings-section">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Geographic Heatmap</h2>
              <p className="text-sm text-gray-500">Signup distribution visualization</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 mb-2">
                <strong>📍 Geographic Insights:</strong>
              </p>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Dashboard displays top 10 countries by signup volume</li>
                <li>• Color intensity indicates signup concentration</li>
                <li>• Hover over countries to see detailed metrics</li>
                <li>• Click influencers in Rankings for location breakdown</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>💡 Use Case:</strong> Identify which countries drive the most conversions. 
                Optimize influencer selection based on geographic performance. Cut budget on 
                influencers who drive traffic from low-converting regions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;